interface RoadmapItem {
  title: string;
  description: string;
  status: "done" | "in-progress" | "planned" | "exploring";
}

const sections: Array<{ title: string; items: RoadmapItem[] }> = [
  {
    title: "v1.0 — Foundation (Shipped)",
    items: [
      { title: "Orchestrator API", description: "Device CRUD, claim/release, test runs, executions, heartbeat", status: "done" },
      { title: "Real-time Dashboard", description: "Fleet overview, test detail with video, live activity feed", status: "done" },
      { title: "Host Agent", description: "ADB device discovery, TCP bridge, health monitoring, cleanup", status: "done" },
      { title: "Google OAuth", description: "@gigsmart.com domain restriction, session management", status: "done" },
      { title: "Batch Device Claiming", description: "Claim up to N devices, flexible availability", status: "done" },
      { title: "APK Install Pipeline", description: "GCS upload, signed URL download, local USB install", status: "done" },
      { title: "Staggered Test Start", description: "Workers launch as devices become ready", status: "done" },
    ],
  },
  {
    title: "v1.2 — gs-hive CLI & DX (Shipped)",
    items: [
      { title: "gs-hive CLI with live dashboard", description: "Personal Google-OAuth tokens, Ink dashboard, interactive claim/release, run suites with live progress", status: "done" },
      { title: "Cloudflared tunnel support", description: "`--api-url` flag — build APK against a local API tunnel so SMS `99999` works", status: "done" },
      { title: "Fuzzy Job Lookup", description: "Search runs by email prefix, job ID, pipeline ID, commit ref — redesigned as a list view", status: "done" },
      { title: "Per-user CLI run tagging", description: "CLI-initiated runs auto-tagged with email prefix in pipelineId/jobId", status: "done" },
      { title: "Cleanup on CLI release", description: "`needs_cleanup` flow wired into the CLI token release endpoint", status: "done" },
      { title: "Pre-populated scenario steps", description: "Failed-Before scenarios now show their full intended step list instead of being empty", status: "done" },
    ],
  },
  {
    title: "v1.2.x — Documentation + Polish (Shipped)",
    items: [
      { title: "API reference at /docs", description: "Full OpenAPI 3.1 spec rendered via Scalar; auth-gated; lazy-loaded to keep main bundle lean.", status: "done" },
      { title: "Architecture + runbook + deployment docs", description: "ARCHITECTURE.md (sequence diagrams), RUNBOOK.md (10 symptom→fix flows), DEPLOYMENT.md, DISASTER_RECOVERY.md, SLOS.md, CONTRIBUTING.md.", status: "done" },
      { title: "Per-package READMEs", description: "ops/device-hive and ops/gs-hive each have entry-point README with quick start, file layout, common workflows.", status: "done" },
      { title: "Curl-able host install", description: "`curl -fsSL https://device-hive.ops.gigsmart.com/install | bash` — bootstraps a fresh Mac Mini with no git clone. Hosts page has click-to-copy panel.", status: "done" },
      { title: "GCS artifacts lifecycle upgrade", description: "From 30-day hard DELETE to tiered STANDARD→NEARLINE→COLDLINE with versioning. Videos from old runs remain accessible forever.", status: "done" },
    ],
  },
  {
    title: "v1.3 — Hive Self-Service (In Progress)",
    items: [
      { title: "Agent + install logs in UI", description: "Live agent stdout on HostDetail; full APK install transcript on DeviceDetail. SSH-free diagnosis.", status: "done" },
      { title: "Auto-migrate on pod startup", description: "schema.sql is now applied on every boot — prevents the kind of drift that broke the dashboard after the v1.3.0 ship.", status: "done" },
      { title: "Force-release stuck claim button", description: "Self-serve recovery for devices stuck in busy/installing — one-click from DeviceDetail instead of curl/kubectl.", status: "planned" },
      { title: "Download all artifacts", description: "One-click .zip download of all videos + screenshots for a run or single scenario. Today you can only watch inline.", status: "planned" },
      { title: "My Activity dashboard", description: "Personal view filtered to current user — runs, claims, recent CLI token usage. Replaces the 'did my run pass?' Slack pings.", status: "planned" },
      { title: "RBAC + audit logging", description: "Admin role for destructive actions (revoke tokens, delete API keys). Audit trail of who-did-what for every mutation.", status: "planned" },
    ],
  },
  {
    title: "v1.4 — CI Integration",
    items: [
      { title: "android-e2e CI Label", description: "Opt-in label for MRs to trigger Android E2E tests", status: "in-progress" },
      { title: "Content-Hash APK Caching on Agent", description: "Skip install when APK hash matches what's on-device already", status: "in-progress" },
      { title: "Mainline Auto-Run", description: "Auto-run on dev/beta/prod with allow_failure: true", status: "planned" },
      { title: "Mac Mini Fleet Provisioning", description: "Set up M1 Mac Mini with 10 devices", status: "in-progress" },
      { title: "CI Result Aggregation", description: "Merged JUnit XML, bulletproof error messages", status: "planned" },
      { title: "Named Cloudflared Tunnels", description: "Stable per-dev URLs at <username>.gigsmart.rocks for local API testing — auto-issued via setup-tunnel wizard.", status: "done" },
      { title: "gs-hive doctor command", description: "Self-diagnose setup issues (cloudflared, auth, ADB, fleet health)", status: "planned" },
      { title: "Bulk device actions", description: "Select N devices in FleetOverview, reboot/clean/quarantine in one click", status: "planned" },
      { title: "Live logs on TestRunDetail", description: "Stream app logcat + detox stderr while the scenario runs", status: "planned" },
    ],
  },
  {
    title: "v1.5 — Security Hardening",
    items: [
      { title: "Rate limiting on auth", description: "express-rate-limit on /auth/* endpoints", status: "planned" },
      { title: "Explicit CORS policy", description: "Allowlist known origins instead of default same-origin-only", status: "planned" },
      { title: "GCS bucket ACL audit", description: "Verify device-hive-artifacts uses uniform bucket-level access; no public ACLs", status: "planned" },
      { title: "Sign out everywhere", description: "User-initiated revocation of all their CLI tokens", status: "planned" },
      { title: "Agent request signing", description: "HMAC on agent → server calls to prevent body tampering", status: "exploring" },
    ],
  },
  {
    title: "v2.0 — Web E2E Integration",
    items: [
      { title: "Web Platform Support", description: "Add 'web' as a platform, show browser info instead of device info", status: "planned" },
      { title: "Playwright Cucumber Hooks", description: "Same Before/AfterStep reporting pattern for web tests", status: "planned" },
      { title: "Web Video Playback", description: "Playwright .webm recordings with step-seeking", status: "planned" },
      { title: "Unified Test Dashboard", description: "Mobile and web E2E results in one place", status: "planned" },
    ],
  },
  {
    title: "Future — Exploring",
    items: [
      { title: "iOS Device Support", description: "idb integration, code signing, Apple Configurator", status: "exploring" },
      { title: "Flaky Test Detection", description: "Track test stability across runs, auto-quarantine flaky scenarios", status: "exploring" },
      { title: "APK Caching on Mini", description: "Cache APKs by hash to avoid re-downloading same build", status: "exploring" },
      { title: "Runner-as-Device for Web", description: "Model CI runners as hosts, browsers as devices for contention management", status: "exploring" },
      { title: "Feature Flag Correlation", description: "Show which flags were active during each test, correlate failures with flag changes", status: "exploring" },
      { title: "Test Performance Trends", description: "Track scenario duration over time, detect regressions", status: "exploring" },
    ],
  },
];

const statusColors: Record<string, { bg: string; text: string; label: string }> = {
  done: { bg: "var(--color-status-available-bg)", text: "var(--color-status-available-text)", label: "Shipped" },
  "in-progress": { bg: "var(--color-status-busy-bg)", text: "var(--color-status-busy-text)", label: "In Progress" },
  planned: { bg: "var(--color-foreground)", text: "var(--color-text-secondary)", label: "Planned" },
  exploring: { bg: "#f0e6ff", text: "#7c3aed", label: "Exploring" },
};

export default function Roadmap() {
  return (
    <div>
      <h1 style={{ fontSize: "var(--font-size-lg)", fontWeight: 700, color: "var(--color-primary)", marginBottom: "var(--space-xl)" }}>
        Roadmap
      </h1>
      {sections.map((section) => (
        <div key={section.title} style={{ marginBottom: "var(--space-xl)" }}>
          <h2 style={{ fontSize: "var(--font-size-md)", fontWeight: 600, color: "var(--color-primary)", marginBottom: "var(--space-md)" }}>
            {section.title}
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-sm)" }}>
            {section.items.map((item) => {
              const s = statusColors[item.status];
              return (
                <div
                  key={item.title}
                  style={{
                    background: "var(--color-surface)",
                    borderRadius: "var(--radius-md)",
                    boxShadow: "var(--shadow-card)",
                    padding: "var(--space-md) var(--space-lg)",
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-md)",
                  }}
                >
                  <span style={{
                    background: s.bg,
                    color: s.text,
                    padding: "2px 8px",
                    borderRadius: "var(--radius-pill)",
                    fontSize: 11,
                    fontWeight: 600,
                    flexShrink: 0,
                    minWidth: 75,
                    textAlign: "center",
                  }}>
                    {s.label}
                  </span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "var(--font-size-base)" }}>{item.title}</div>
                    <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>{item.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
